"use client";
import { useEffect, useState } from "react";

interface UseDebouncedValueOptions {
  leading?: boolean;
}

/**
 * Hook that debounces a value.
 * @param value - The value to debounce
 * @param wait - The debounce delay in milliseconds
 * @param options - Options object with `leading` flag
 * @returns Tuple of [debouncedValue]
 */
export function useDebouncedValue<T>(
  value: T,
  wait: number,
  options?: UseDebouncedValueOptions
): [T] {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const leading = options?.leading ?? false;

  useEffect(() => {
    // If leading is true and this is the first render, use the value immediately
    if (leading) {
      setDebouncedValue(value);
    }

    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, wait);

    return () => {
      clearTimeout(handler);
    };
  }, [value, wait, leading]);

  return [debouncedValue];
}
